import { ColumnFiltersState } from '@tanstack/react-table';
import { DateTime } from 'luxon';

/** Event-like shape with status and startDateTime (Event, TicketedEvent) */
type EventWithStatus = { status?: string; startDateTime?: string | null };

/**
 * Effective status sort key for events: PAST_EVENT (0) < PUBLISHED (1) < DRAFT (2).
 * Use for sorting event lists by displayed status (Past Event, Published, Draft).
 */
export function getStatusSortKey(event: EventWithStatus): number {
  if (event.status === 'DRAFT') return 2;
  if (event.status === 'PUBLISHED') {
    const isPast = event.startDateTime
      ? DateTime.fromISO(event.startDateTime) < DateTime.now()
      : false;
    return isPast ? 0 : 1;
  }
  return 3;
}

export function formatPhoneNumber(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (digits.length === 10) {
    return digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }
  return input;
}

export function formatNumberWithCommas(
  value: number | string | null | undefined,
): string {
  if (value === undefined || value === null) return '';

  const raw = String(value).trim();
  if (!raw) return '';

  const cleaned = raw.replace(/,/g, '');

  const num = Number(cleaned);
  if (Number.isNaN(num)) {
    return '';
  }

  const [intPart, decPart] = cleaned.split('.');

  const formattedInt = Number(intPart).toLocaleString('en-US');

  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
}

/**
 * Formats a number into US dollar currency format.
 *
 * @param {number} number - The numeric value to format.
 * @returns {string} The formatted currency string (e.g., "$1,234.56").
 */
export function formatCurrency(number: number | null | undefined): string {
  if (number === undefined || number === null) return '';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(number);
}

/**
 * Builds a record set of filter parameters from the given column filters.
 * Used for constructing API query parameters from the UI kit table filters.
 */
export function buildFilterParams(
  filters: ColumnFiltersState,
): Record<string, any> {
  const params: Record<string, any> = {};

  filters.forEach((cf) => {
    if (!cf?.id || !cf?.value) return;
    const { operator, value } = cf.value as any;
    if (value == null || value === '') return;

    const key = `filter[${cf.id}]`;
    const makePattern = (raw: string) => {
      switch (operator) {
        case 'contains':
          return `*${raw}*`;
        case 'startsWith':
          return `${raw}*`;
        case 'endsWith':
          return `*${raw}`;
        case 'gt':
          return `>${raw}`;
        case 'gte':
          return `>=${raw}`;
        case 'lt':
          return `<${raw}`;
        case 'lte':
          return `<=${raw}`;
        case 'isNull':
          return 'null';
        case 'isNotNull':
          return '!null';
        case 'not':
        case 'neq':
          return `!${raw}`;
        default:
          return raw;
      }
    };

    const values = Array.isArray(value) ? value : [value];
    params[key] = values.map((v) => makePattern(String(v))).join(',');
  });

  return params;
}

/**
 * Formats a date into a short US-style string (e.g., "Apr 10, 2026"); returns "N/A" if no date is provided.
 */
export function formatDate(date) {
  if (!date) return 'N/A';

  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a date into "MMM D, YYYY, h:mm AM/PM" (en-US locale); returns "N/A" if no date is provided.
 */
export function formatDateWithTime(date) {
  if (!date) return 'N/A';

  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function toDate(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Returns an ISO string for the given date/time string, or null if empty/invalid.
 */
export function toISOorNull(v?: string): string | null {
  return v && v.trim() ? new Date(v).toISOString() : null;
}

/**
 * Converts a datetime-local value (e.g. "2025-02-22T17:00") to ISO UTC,
 * interpreting the value in the given timezone so that "5pm" means 5pm in that
 * timezone (e.g. venue timezone), not the user's local time.
 */
export function dateTimeLocalToZoneISO(
  dateTimeLocal: string,
  timeZone: string,
): string | null {
  if (!dateTimeLocal?.trim()) return null;
  const dt = DateTime.fromISO(dateTimeLocal.trim(), { zone: timeZone });
  if (!dt.isValid) return null;
  return dt.toUTC().toISO();
}

/**
 * Returns trimmed string or null if empty/whitespace.
 */
export function toTrimmedOrNull(v?: string): string | null {
  return v && v.trim().length ? v.trim() : null;
}

export function computeNextSubscriptionRenewal(sub: any): string | null {
  if (!sub) return null;
  const start = toDate(sub.subscriptionRenewedAt) || toDate(sub.beginsAt);
  const unit = sub.subscriptionUnit;
  const duration = Number(sub.subscriptionDuration || 0);
  if (!start || !unit || !duration) return null;

  const next = new Date(start);
  if (unit === 'DAY') next.setDate(next.getDate() + duration);
  else if (unit === 'MONTH') next.setMonth(next.getMonth() + duration);
  else if (unit === 'YEAR') next.setFullYear(next.getFullYear() + duration);
  else return null;

  return next.toISOString();
}

export function computeNextWalletRenewal(sub: any): string | null {
  if (!sub) return null;
  const start = toDate(sub.walletRenewedAt) || toDate(sub.beginsAt);
  const unit = sub.walletUnit;
  const duration = Number(sub.walletDuration || 0);
  if (!start || !unit || !duration) return null;

  const next = new Date(start);
  if (unit === 'DAY') next.setDate(next.getDate() + duration);
  else if (unit === 'MONTH') next.setMonth(next.getMonth() + duration);
  else if (unit === 'YEAR') next.setFullYear(next.getFullYear() + duration);
  else return null;

  return next.toISOString();
}

export const formatZonedDateTime = (
  dateTime: string | null | undefined,
  format: string,
  timeZone: string = 'America/New_York',
): string => {
  if (!dateTime) return '';

  return (
    DateTime
      // parse the ISO string as UTC (because it ends in Z)
      .fromISO(dateTime, { zone: 'utc' })
      // convert to the target timezone
      .setZone(timeZone)
      // format however the caller wants
      .toFormat(format)
  );
};

export function formatPhoneNumberFriendlyDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, '');

  const local = digits.length === 11 ? digits.slice(1) : digits;

  if (local.length !== 10) {
    return phone;
  }

  return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
}
